/* eslint-disable react/jsx-no-useless-fragment */
import { AccelFile } from "@axl-dev/school-sdk";
import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLoc } from "@app/shared/lib/hooks";
import { useClickOutside } from "@app/shared/lib/hooks/useClickOutside";

import styles from "./FileName.module.scss";

type Props = {
  file: AccelFile;
  useExtension: boolean;
  editable: boolean;
  onRename: (name: string) => Promise<boolean>;
  className?: string;
};

export const FileName: React.FC<Props> = ({
  file,
  editable,
  useExtension,
  className,
  onRename,
}) => {
  const loc = useLoc();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const save = useCallback(async () => {
    const val = inputRef.current?.value ?? "";
    if (val !== file.name) {
      setSaving(true);
      await onRename(val);
      setSaving(false);
    }

    setEditing(false);
  }, [file.name, onRename]);

  useClickOutside(inputRef, editing, () => {
    save();
  });

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  return (
    <>
      {!editing ? (
        <div
          className={cn(
            "ellipsis",
            editable ? "c-pointer hoverable" : undefined,
            className,
          )}
          onClick={() => editable && setEditing(true)}
          title={
            editable
              ? loc.word("File.clickToRename", { default: "Click to rename" })
              : file.filename
          }
        >
          {useExtension === true ? file.filename : file.name}
        </div>
      ) : (
        <input
          ref={inputRef}
          defaultValue={file.name}
          type="text"
          disabled={saving}
          className={cn(styles.fl_filename_input, "w-100 p-0 m-0 no-border")}
          onKeyUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === "Enter") save();
          }}
        />
      )}
    </>
  );
};
